import {useRef, useEffect, useState} from 'react'
import _ from 'lodash'
import {Modal} from 'semantic-ui-react'
import MonacoEditor, {Monaco, EditorProps} from '@monaco-editor/react'
import {editor} from 'monaco-editor/esm/vs/editor/editor.api'
import {ExpressionParams, IParamData} from '../params'
import './style.css'

export interface IExpressionConfig {
  expression: string
  params?: {
    [key: string]: IParamData
  }
}

export interface IExpressionEditorProps extends Omit<EditorProps, 'value' | 'onChange'> {
  instanceID?: string
  value?: IExpressionConfig
  actions?: React.ReactNode
  showParams?: boolean
  showExpression?: boolean
  isEditableAsJson?: boolean
  fitToContent?: boolean
  options?: EditorProps['options']
  onRunExpression?: (expression: IExpressionConfig) => void
  onChange?: (expression: IExpressionConfig) => void
}

export function ExpressionEditor(props: IExpressionEditorProps) {
  const {
    value,
    actions,
    options,
    showParams,
    showExpression,
    isEditableAsJson,
    fitToContent,
    onRunExpression,
    onChange,
    ...rest
  } = {
    showExpression: true,
    ...props,
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const monacoRef = useRef<Monaco>()
  const editorRef = useRef<editor.IStandaloneCodeEditor>()

  const [paramData, setParamData] = useState<{[key: string]: IParamData}>({})
  const [currentJson, setCurrentJson] = useState<string>('')

  useEffect(() => {
    if (_.isEqual(paramData, value?.params)) return
    setParamData(value?.params || {})
  }, [value])

  useEffect(() => {
    if (!editorRef.current) return
    const expression = _.trim(editorRef.current.getValue())
    const _e: IExpressionConfig = {
      expression: expression || '',
      params: paramData,
    }
    if (onChange) onChange(_e)
    handleOnRunExpression(_e)
  }, [paramData])

  useEffect(() => {
    if (!editorRef.current) return
    const monaco = monacoRef.current
    if (!monaco) return
    const _action = editorRef.current.addAction({
      id: 'run-expression',
      label: 'Run expression',
      keybindings: [
        monaco.KeyCode.Enter,
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
        // monaco.KeyMod.chord(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_K, monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_M)
      ],
      precondition: undefined,
      keybindingContext: undefined,
      contextMenuGroupId: 'navigation',
      contextMenuOrder: 1.5,
      run: () => {
        if (!editorRef.current) return
        handleOnRunExpression({
          expression: _.trim(editorRef.current.getValue()),
          params: paramData,
        })
        return
      },
    })
    const _sizeChange = editorRef.current.onDidContentSizeChange(handleOnDidContentSizeChange)
    return () => {
      _action.dispose()
      _sizeChange.dispose()
    }
  }, [onRunExpression, editorRef.current])

  function handleOnRunExpression(e: IExpressionConfig) {
    if (onRunExpression) onRunExpression(e)
  }

  function handleOnDidContentSizeChange() {
    if (!fitToContent) return
    if (!containerRef.current) return
    if (!editorRef.current) return
    const containerWidth = containerRef.current.clientWidth
    const contentHeight = editorRef.current.getContentHeight()
    try {
      editorRef.current.layout({
        width: containerWidth,
        height: Math.max(contentHeight, 25),
      })
    } catch (error) {
      console.error('error in handleOnDidContentSizeChange', error)
    }
  }

  return (
    <div
      ref={containerRef}
      className='expression-editor'
    >
      {showParams ?
        <ExpressionParams
          actions={
            <>
              {isEditableAsJson ?
                <Modal
                  size='tiny'
                  trigger={<div>json</div>}
                  onOpen={() => {
                    if (!editorRef.current) return
                    setCurrentJson(
                      JSON.stringify(
                        {
                          expression: editorRef.current.getValue(),
                          params: paramData,
                        },
                        undefined,
                        2
                      )
                    )
                  }}
                  onClose={() => {
                    try {
                      const {expression, params} = JSON.parse(currentJson) as {
                        expression: string
                        params: {[key: string]: IParamData}
                      }
                      if (onChange) onChange({expression, params})
                      setParamData(params)
                    } catch (error) {
                      console.error('invalid json', currentJson, error)
                      return
                    }
                  }}
                >
                  <Modal.Content>
                    <textarea
                      autoFocus
                      value={currentJson}
                      onChange={(e) => setCurrentJson(e.target.value || '')}
                      onFocus={(e) => e.target.select()}
                      style={{
                        width: '100%',
                        minWidth: '100%',
                        maxWidth: '100%',
                        minHeight: '200px',
                      }}
                    />
                  </Modal.Content>
                </Modal>
              : undefined}
              {actions}
            </>
          }
          paramData={paramData}
          onChange={setParamData}
        />
      : null}
      {showExpression ?
        <MonacoEditor
          height={fitToContent ? undefined : 40}
          theme='vs-dark'
          options={{
            scrollBeyondLastLine: false,
            fixedOverflowWidgets: true,
            minimap: {
              enabled: false,
            },
            scrollbar: {
              alwaysConsumeMouseWheel: fitToContent ? false : true,
            },
            ...options,
          }}
          onMount={(editor, monaco) => {
            monacoRef.current = monaco
            editorRef.current = editor
            handleOnDidContentSizeChange()
          }}
          onChange={(value) => {
            if (onChange) onChange({expression: value || '', params: paramData})
          }}
          value={value?.expression || ''}
          {...rest}
        />
      : null}
    </div>
  )
}
